<template>
    <v-container class="grey lighten-5" fluid>

        <v-progress-linear
            absolute
            bottom
            :active="pageLoading"
            :indeterminate="true"
            height="4"
            color="primary"
        ></v-progress-linear>

        <v-row v-if="client && client.migrated" no-gutters :class="{ 'px-4': $vuetify.breakpoint.xs}" class="mt-4">
            <v-col cols="12" class="d-flex align-center justify-center">
                <v-alert dense type="info"> This client has been migrated</v-alert>
            </v-col>
        </v-row>

        <v-row v-if="client">
            <v-col md="6">
                <v-card>
                    <!-- Client Information  -->
                    <v-card-title class="px-3">Client Information</v-card-title>
                    <v-divider></v-divider>

                    <v-form ref="form" lazy-validation @submit="updateClient">
                        <v-row class="px-4 py-2">
                            <!-- Company -->
                            <v-col md="12">
                                <v-text-field
                                    v-model="client.merchant.name"
                                    label="Company"
                                    readonly
                                ></v-text-field>
                            </v-col>

                            <!-- First Name -->
                            <v-col md="4" class="pt-0">
                                <v-text-field
                                    v-model="client.firstName"
                                    label="First Name"
                                    :rules="[rules.required]"
                                    maxlength="32"
                                ></v-text-field>
                            </v-col>

                            <!-- Surname -->
                            <v-col md="4" class="pt-0">
                                <v-text-field
                                    v-model="client.surname"
                                    label="Surname"
                                    maxlength="32"
                                ></v-text-field>
                            </v-col>

                            <!-- Reference -->
                            <v-col md="4" class="pt-0">
                                <v-text-field
                                    v-model="client.merchantCustomerId"
                                    label="Reference"
                                    readonly
                                ></v-text-field>
                            </v-col>

                            <!-- Email -->
                            <v-col md="5" class="pt-0">
                                <v-text-field
                                    v-model="client.email"
                                    label="Email"
                                    :rules="[validEmail]"
                                    maxlength="128"
                                ></v-text-field>
                            </v-col>

                            <!-- Mobile -->
                            <v-col md="3" class="pt-0">
                                <v-text-field
                                    v-model="client.mobile"
                                    label="Mobile"
                                    :rules="[mobileCheck]"
                                    maxlength="15"
                                ></v-text-field>
                            </v-col>

                            <!-- Dob -->
                            <v-col md="4" class="pt-0">
                                <v-menu
                                    ref="clientDobMenu"
                                    v-model="dobField.dialogue.menu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                    >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                        v-model="client.dob"
                                        label="Date of Birth"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="dobField.rules"
                                        :clearable="dobField.clearable"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="client.dob"
                                        :active-picker.sync="dobField.dialogue.activePicker"
                                        :max="(new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)"
                                        min="1950-01-01"
                                        @change="save"
                                    ></v-date-picker>
                                    </v-menu>
                            </v-col>

                            <!-- Street 1 -->
                            <v-col md="8" class="pt-0">
                                <v-text-field
                                    v-model="client.street1"
                                    label="Street 1"
                                    :rules="[rules.required]"
                                    maxlength="32"
                                ></v-text-field>
                            </v-col>

                            <!-- City -->
                            <v-col md="4" class="pt-0">
                                <v-text-field
                                    v-model="client.city"
                                    label="City"
                                    :rules="[rules.required]"
                                    maxlength="32"
                                ></v-text-field>
                            </v-col>

                            <!-- Street 2 -->
                            <v-col md="8" class="pt-0">
                                <v-text-field
                                    v-model="client.street2"
                                    label="Street 2"
                                ></v-text-field>
                            </v-col>

                            <!-- Postcode -->
                            <v-col md="4" class="pt-0">
                                <v-text-field
                                    v-model="client.postcode"
                                    label="Postcode"
                                    :rules="[rules.required]"
                                    maxlength="7"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" @click="showClientLogComponent = true">Client Log</v-btn>
                            <v-btn color="primary" type="submit">Update Client</v-btn>
                        </v-card-actions>
                    </v-form>
                </v-card>
            </v-col>

            <!-- Client Log -->
            <client-log
                :visible="showClientLogComponent"
                :clientLog="client.client_log"
                @close="showClientLogComponent = $event"
                @getClientLog="getClientLog"
            ></client-log>

            <!-- Registered Cards -->
            <v-col md="6">
                <v-card>
                    <v-card-title class="px-3">Registered Cards
                        <v-spacer></v-spacer>
                        <v-menu bottom offset-y v-if="cfgChannel !== null">
                            <template v-slot:activator="{ on }">
                                <v-btn 
                                    text
                                    icon 
                                    color="#757575"
                                    v-on="on"
                                    ><v-icon color="#757575" size="22">
                                        mdi-menu
                                    </v-icon>
                                </v-btn>
                            </template>

                            <v-list>
                                <!-- <v-list-item @click="registerCardDialog">
                                    <v-list-item-title>
                                        <v-icon small class="pr-3" color="primary">mdi-credit-card-outline</v-icon>Register Card
                                    </v-list-item-title>
                                </v-list-item> -->
                                <v-list-item @click="showRegisterCardCnpComponent = true" v-if="cfgChannel == 'MOTO:ECOM' || cfgChannel == 'MOTO' || cfgChannel == 'All'">
                                    <v-list-item-title>
                                        <v-icon small class="pr-3" color="primary">mdi-credit-card-outline</v-icon>Register Card
                                    </v-list-item-title>
                                </v-list-item>
                                <!-- <v-list-item @click="sendRegLink(); snackbars.sendRegLink = true;">
                                    <v-list-item-title>
                                        <v-icon small class="pr-3" color="success">mdi-email-send-outline</v-icon>Send Registration Link
                                    </v-list-item-title>
                                </v-list-item> -->
                                <v-list-item @click="sendRegLinkDialog" v-if="cfgChannel == 'ECOM' || cfgChannel == 'All'">
                                    <v-list-item-title>
                                        <v-icon small class="pr-3" color="success">mdi-email-send-outline</v-icon>Send Registration Link
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>

                        <!-- Register Card CNP -->
                        <register-card-cnp
                            v-if="showRegisterCardCnpComponent"
                            :visible="showRegisterCardCnpComponent"
                            :client="client"
                            :channels="channels"
                            @close="showRegisterCardCnpComponent = $event"
                            @getCards="getCards"
                            @getClientLog="getClientLog"
                        ></register-card-cnp>

                        <!-- Register Card via Link !-->
                        <register-card-link
                            :visible="showRegisterCardLinkComponent"
                            :client="client"
                            :channels="channels"
                            @close="showRegisterCardLinkComponent = $event"
                        ></register-card-link>

                        <!-- Snackbar - Registration link sent -->
                        <v-snackbar v-model="snackbars.sendRegLink" color="primary">
                            Registration link sent.
                            <v-icon color="white" @click="snackbars.sendRegLink = false">mdi-close-circle</v-icon>
                        </v-snackbar>
                        <v-snackbar v-model="snackbars.cardAdded" color="primary">
                            Card successfully added.
                            <v-icon color="white" @click="snackbars.cardAdded = false">mdi-close-circle</v-icon>
                        </v-snackbar>
                        <!-- Snackbar - Registration failed -->
                        <v-snackbar v-model="snackbars.cardRegErr.show" color="error">
                            <b>Error registering card: </b>
                            <span>code: {{ snackbars.cardRegErr.code }}</span>
                            <v-icon color="white" @click="snackbars.cardRegErr.show = false">mdi-close-circle</v-icon>
                        </v-snackbar>
                    </v-card-title>
                    
                    <v-divider></v-divider>
                    
                    <v-col class="py-0 px-4 archive-switch">
                        <v-switch
                            v-model="cardsTable.showArchived"
                            dense
                            label="Archived"
                            @change="archiveCardSwitch"
                        ></v-switch>
                    </v-col>
                
                    <v-data-table
                        v-if="cardsTable.data"
                        :headers="cardsTable.headers"
                        :items="cardsTable.data"
                        :items-per-page="6"
                        class="elevation-0"
                        hide-default-footer
                    >
                        <template v-slot:item.state="{ item }">
                            <v-icon v-if="item.state == 'active'" color="success" size="22">
                                mdi-check-circle
                            </v-icon>
                            <v-icon v-if="item.state == 'expired'" color="error" size="22">
                                mdi-alert-circle
                            </v-icon>
                        </template>
                        <template v-slot:item.number="{ item }">
                            {{ `${item.card.bin}******${item.card.last4Digits}` }}
                        </template>
                        <template v-slot:item.expiry="{ item }">
                            {{ `${item.card.expMonth}/${item.card.expYear}` }}
                        </template>
                        <template v-slot:item.action="{ item }">
                            <v-menu bottom offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn 
                                        text
                                        icon 
                                        color="#757575"
                                        v-on="on"
                                        ><v-icon color="#757575" size="22">
                                            mdi-credit-card-outline
                                        </v-icon>
                                    </v-btn>
                                </template>

                                <v-list>
                                    <v-list-item @click="archiveCard(item)">
                                        <v-list-item-title>
                                            <v-icon small class="pr-3">mdi-archive-arrow-down</v-icon>
                                            <span v-if="!cardsTable.showArchived">Archive Card</span>
                                            <span v-else>Unarchive Card</span>
                                            
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item v-if="!cardsTable.showArchived && item.state == 'active'" @click="createScheduleDialog(item)">
                                        <v-list-item-title>
                                            <v-icon small class="pr-3" color="success">mdi-calendar-month</v-icon>Create Schedule
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item v-if="(!cardsTable.showArchived && item.state == 'active') && (cfgChannel == 'MOTO:ECOM' || cfgChannel == 'MOTO' || cfgChannel == 'All')" @click="takePaymentDialog(item)">
                                        <v-list-item-title>
                                            <v-icon small class="pr-3" color="primary">mdi-credit-card-outline</v-icon>Take Payment
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table>

                    <!-- Create Schedule !-->
                    <create-schedule
                        :visible="createScheduleComponent.show"
                        :cardData="createScheduleComponent.data"
                        :client="client"
                        :channels="channels"
                        @close="createScheduleComponent.show = $event"
                        @getProducts="getProducts"
                        @getSchedules="getSchedules"
                        @getClientLog="getClientLog"
                    ></create-schedule>

                    <!-- Payment -->
                    <payment
                        :visible="takePaymentComponent.show"
                        :cardData="takePaymentComponent.data"
                        :client="client"
                        :channels="channels"
                        @close="takePaymentComponent.show = $event"
                        @getPayments="getPayments"
                        @getProducts="getProducts"
                    ></payment>
                </v-card>
            </v-col>

            <!-- Schedules -->
            <v-col md="12">
                <v-card>

                    <v-card-title class="px-3">Schedules</v-card-title>
                    <v-divider></v-divider>

                    <v-data-table
                        :headers="schedulesTable.headers"
                        :items="schedulesTable.data"
                        class="elevation-0"
                        v-if="schedulesTable.data && cardsTable.data"
                    >
                        <template v-slot:item.status="{ item }">
                            <!-- Active Schedules -->
                            <v-chip
                                v-if="item.status == 1 && (item.arrears == 0 || item.arrears == null) && (item.startDate <= now)"
                                class="ma-2"
                                color="success"
                                small
                                outlined
                            >
                                <v-avatar left><v-icon small>mdi-check-circle</v-icon></v-avatar>Up-to-date
                            </v-chip>
                            <v-chip
                                v-if="item.status == 1 && item.arrears > 0 && (item.startDate <= now)"
                                class="ma-2"
                                color="error"
                                small
                                outlined
                            >
                                <v-avatar left><v-icon small>mdi-check-circle</v-icon></v-avatar>In arrears
                            </v-chip>

                            <!-- Future Schedules -->
                            <v-chip
                                v-if="item.status == 1 && (item.arrears == 0 || item.arrears == null) && (item.startDate > now)"
                                class="ma-2"
                                color="primary"
                                small
                                outlined
                            >
                                <v-avatar left><v-icon small>mdi-check-circle</v-icon></v-avatar>Future
                            </v-chip>

                            <!-- Terminated Schedules -->
                            <v-tooltip v-if="item.status == 0 && (item.arrears == 0 || !item.arrears)" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-chip                                
                                        class="ma-2"
                                        color="error"
                                        text-color="white"
                                        small
                                        v-on="on"
                                    >
                                        <v-avatar left><v-icon small>mdi-close-circle</v-icon></v-avatar>Up-to-date
                                    </v-chip>
                                </template>
                                <span v-if="item.cancelledByName">Cancelled By: {{ item.cancelledByName }}</span>
                                <span v-else>Cancelled By: SYSTEM</span>
                            </v-tooltip>
                                
                            <v-tooltip v-if="item.status == 0 && item.arrears > 0" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-chip
                                        class="ma-2"
                                        color="error"
                                        text-color="white"
                                        small
                                        v-on="on"
                                    >
                                        <v-avatar left><v-icon small>mdi-close-circle</v-icon></v-avatar>In Arrears
                                    </v-chip>
                                </template>
                                <span v-if="item.cancelledByName">Cancelled By: {{ item.cancelledByName }}</span>
                                <span v-else>Cancelled By: SYSTEM</span>
                            </v-tooltip>
                        </template>
                        <template v-slot:item.plan="{ item }">
                            <b>{{ `#${item.id}` }}</b> - {{ `${item.frequency} @ ${item.collectionDay}` }}
                        </template>
                        <template v-slot:item.amount="{ item }">
                            {{ `${item.currency} ${item.amount}`}}
                        </template>
                        <template v-slot:item.cardNumber="{ item }">
                            <v-btn 
                                v-if="item.status == 1"
                                text
                                icon 
                                color="#757575"
                                @click="changeCard(item)"
                                >
                                <v-icon small color="primary" size="22">
                                    mdi-sync-circle
                                </v-icon>
                            </v-btn>
                            <v-btn 
                                v-else
                                text
                                icon 
                                color="#757575"
                                >
                                <v-icon small color="error" size="22">
                                    mdi-sync-circle
                                </v-icon>
                            </v-btn>
                            {{ `${item.bin}******${item.last4Digits}`}}
                        </template>
                        <template v-slot:item.createdAt="{ item }">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon small color="primary" size="22" class="mr-2" v-on="on">
                                        mdi-information
                                    </v-icon>
                                </template>
                                Created By: {{ item.createdByName || 'API' }}
                            </v-tooltip>
                            {{ item.createdAt }}
                        </template>
                        <template v-slot:item.action="{ item }">
                            <v-menu v-if="item.status == 1" bottom offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn 
                                        text
                                        icon 
                                        color="#757575"
                                        v-on="on"
                                        ><v-icon color="#757575" size="22">
                                            mdi-cursor-pointer
                                        </v-icon>
                                    </v-btn>
                                </template>

                                <v-list>
                                    <v-list-item v-if="item.status == 1" @click="terminate(item)">
                                        <v-list-item-title>
                                            <v-icon small color="error" class="pr-3">mdi-close-circle</v-icon>Terminate
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item v-if="!cardsTable.showArchived" @click="scheduleSummary(item)">
                                        <v-list-item-title>
                                            <v-icon small class="pr-3" color="primary">mdi-clipboard-list</v-icon>Schedule Summary
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table>

                    <v-progress-linear
                        color="deep-purple accent-4"
                        indeterminate
                        height="6"
                        :active="schedulesTable.loading"
                    ></v-progress-linear>

                    <!-- Change Card -->
                    <change-card
                        :visible="showChangeCardComponent"
                        :cards="cardsTable.data"
                        :curCard="cardsTable.selectedCard"
                        @close="showChangeCardComponent = $event"
                        @getSchedules="getSchedules"
                        @getClientLog="getClientLog"
                    ></change-card>

                    <!-- Schedule Summary -->
                    <schedule-summary
                        :visible="scheduleSummaryComponent.show"
                        :client="client"
                        :cards="cardsTable"
                        :scheduleData="scheduleSummaryComponent.data"
                        :channels="this.channels"
                        :cfgChannel="cfgChannel"
                        @close="scheduleSummaryComponent.show = $event"
                        @getSchedules="getSchedules"
                        @updatePayments="scheduleSummary"
                        @getClientLog="getClientLog"
                    ></schedule-summary>
                </v-card>
            </v-col>


            <!-- Payment History -->
            <v-row>
                <v-col md="12">
                    <v-card>
                        <v-card-title class="px-3">Payment History</v-card-title>
                        <v-divider></v-divider>

                        <v-data-table
                            v-if="paymentsTable.data"
                            :headers="paymentsTable.headers"
                            :items="paymentsTable.data"
                            class="elevation-0"
                            :items-per-page="10"
                        >
                            <template v-slot:item.status="{ item }">
                                <v-icon v-if="item.status == 1" small color="success" size="22">
                                    mdi-check-circle
                                </v-icon>
                                <v-icon v-else-if="item.status == 0" small color="error" size="22">
                                    mdi-close-circle
                                </v-icon>
                                <v-icon v-if="item.status == 2" small color="success" size="22">
                                    mdi-progress-clock
                                </v-icon>
                            </template>
                            <template v-slot:item.source="{ item }">
                                <span v-if="item.scheduleId">
                                    Schedule ID: {{ `#${item.scheduleId}`}}<br>
                                    Payment ID: {{ `#${item.paymentId}`}}<br>
                                    {{ `${item.source}`}}
                                </span>
                                <span v-else>
                                    Standalone<br>
                                    {{ `${item.source}`}}
                                </span>
                            </template>
                            <template v-slot:item.amount="{ item }">
                                <span v-if="item.currency">
                                    {{ `${item.currency} ${item.amount}`}}
                                </span>
                                <span v-else>
                                    {{ `${item.amount}`}}
                                </span>
                            </template>
                            <template v-slot:item.cardNumber="{ item }">
                                {{ `${item.bin}******${item.last4Digits}`}}
                            </template>
                            <template v-slot:item.action="{ item }">
                                <v-menu v-if="item.status == 1" bottom offset-y>
                                    <template v-slot:activator="{ on }">
                                        <v-btn 
                                            text
                                            icon 
                                            color="#757575"
                                            v-on="on"
                                            ><v-icon color="#757575" size="22">
                                                mdi-cursor-pointer
                                            </v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item v-if="item.status == 1" @click="refund(item)">
                                            <v-list-item-title>
                                                <v-icon small color="primary" class="pr-3">mdi-credit-card-refund-outline</v-icon>Refund
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </template>
                        </v-data-table>

                        <v-progress-linear
                            color="deep-purple accent-4"
                            indeterminate
                            height="6"
                            :active="paymentsTable.loading"
                        ></v-progress-linear>
                    </v-card>
                </v-col>
            </v-row>
        </v-row>
            
    </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import CreateSchedule from '@/components/schedules/CreateSchedule.vue';
import ScheduleSummary from '@/components/schedules/ScheduleSummary.vue';
import RegisterCardCNP from '@/components/schedules/RegisterCardCNP.vue';
import Payment from '@/components/schedules/Payment.vue';
import ClientLog from '@/components/schedules/ClientLog.vue';
import ChangeCard from '@/components/schedules/ChangeCard.vue';
import RegisterCardLink from '@/components/schedules/RegisterCardLink.vue';

export default {
    components: {
        'create-schedule': CreateSchedule,
        'schedule-summary': ScheduleSummary,
        'register-card-cnp': RegisterCardCNP,
        'payment': Payment,
        'client-log': ClientLog,
        'change-card': ChangeCard,
        'register-card-link': RegisterCardLink,
    },
    data() {
        return {
            rules: {
                required: v => !!v || 'Required.'
            },
            client: null,
            path: null,
            channels: null,
            cfgChannel: null,

            dobField: {
                required: false,
                clearable: true,
                rules: [],
                dialogue: {
                    activePicker: null,
                    menu: false,
                },
            },

            showRegisterCardComponent: false,
            showClientLogComponent: false,
            showChangeCardComponent: false,
            showRegisterCardCnpComponent: false,
            showRegisterCardLinkComponent: false,

            createScheduleComponent: {
                show: false,
                data: null,
            },

            scheduleSummaryComponent: {
                show: false,
                data: null,
            },

            takePaymentComponent: {
                show: false,
                data: null,
            },

            cardsTable: {
                headers: [
                    { text: 'Status', value: 'state', sortable: false, align: 'center' },
                    { text: 'Card Number', value: 'number', sortable: false, align: 'left' },
                    { text: 'Expiry Date', value: 'expiry', sortable: false, align: 'left' },
                    { text: 'Card Holder', value: 'card.holder', sortable: false, align: 'left' },
                    { text: 'Action', value: 'action', sortable: false, align: 'center' }
                ],
                data: null,
                selectedCard: null,
                showArchived: false,
                archived: [],
                unarchived: [],
                loading: true,
            },

            schedulesTable: {
                headers: [
                    { text: 'Status', value: 'status', sortable: false, align: 'center' },
                    { text: 'Plan', value: 'plan', sortable: false, align: 'left' },
                    { text: 'Product', value: 'productName', sortable: false, align: 'left' },
                    { text: 'Amount', value: 'amount', sortable: false, align: 'center' },
                    { text: 'Card Number', value: 'cardNumber', sortable: false, align: 'center' },
                    { text: 'Created At', value: 'createdAt', sortable: false, align: 'center' },
                    { text: 'Start Date', value: 'startDate', sortable: false, align: 'center' },
                    { text: 'End Date', value: 'endDate', sortable: false, align: 'center' },
                    { text: 'Previous Payment', value: 'previousExecution', sortable: false, align: 'center' },
                    { text: 'Next Execution', value: 'nextExecution', sortable: false, align: 'center' },
                    { text: 'Action', value: 'action', sortable: false, align: 'center' }
                ],
                data: null,
                loading: true,
            },

            paymentsTable: {
                headers: [
                    { text: 'Status', value: 'status', sortable: false, align: 'center' },
                    { text: 'Date', value: 'processingTime', sortable: false, align: 'left' },
                    { text: 'Type', value: 'paymentType', sortable: false, align: 'left' },
                    { text: 'Source', value: 'source', sortable: false, align: 'left' },
                    { text: 'Product', value: 'name', sortable: false, align: 'left' },
                    { text: 'Result', value: 'resultDescription', sortable: false, align: 'left' },
                    { text: 'Amount', value: 'amount', sortable: false, align: 'center' },
                    { text: 'Card Number', value: 'cardNumber', sortable: false, align: 'center' },
                    { text: 'Card Holder', value: 'holder', sortable: false, align: 'center' },
                    { text: 'Action', value: 'action', sortable: false, align: 'center' }
                ],
                data: null,
                loading: true,
            },

            snackbars: {
                sendRegLink: false,
                cardAdded: false,
                cardRegErr: {show: false, code: null, description: null},
            },
        }
    },
    beforeMount() {
        this.accessed();
        this.getClient();
        this.getSchedules();
        this.getPayments();
    },
    computed: {
        now() {
            return moment().format('YYYY-MM-DD h:i:s');
        },

        pageLoading() {
            return this.schedulesTable.loading || this.paymentsTable.loading || this.cardsTable.loading;
        }
    },
    watch: {
        "dobField.dialogue.menu" (val) {
            val && setTimeout(() => (this.dobField.dialogue.activePicker = 'YEAR'))
        },
        "dobField.required" (required) {
            if (required) {
                this.dobField.rules = [this.rules.required];
                this.dobField.clearable = false;
            } else {
                this.dobField.rules = [];
                this.dobField.clearable = true;
            }
        },
    },
    methods: {
        save (date) {
            this.$refs.clientDobMenu.save(date)
        },
        getClient() {
            axios({
            method: 'post',
            url: `get-client`,
            data: { clientId: this.$route.params.id },
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                console.log(response.data);
                this.client = response.data;
                console.log('client', this.client);
                this.client.mobile = this.client.mobile ? this.client.mobile : null;

                // Configure active and expired cards
                this.filterStoredCards(response.data.registration);
                this.cardsTable.data = this.cardsTable.unarchived;
                this.cardsTable.loading = false;

                // Get CRM config channels
                var crmCfg = response.data.crm_config;
                this.channels = crmCfg;
                this.parseChannelCfg(crmCfg);

                // set dob field params from crm config
                var dobRequired = Boolean(crmCfg.captureDob);
                this.dobField.required = dobRequired;
                this.dobField.clearable = !dobRequired;

                if(this.$route.query.resourcePath) {
                    var rPath = this.$route.query.resourcePath;

                    if(rPath.constructor === Array) {
                        rPath = rPath[rPath.length - 1];
                    }

                    this.path = rPath;
                    this.getRegStatus();
                }
            })
            .catch(e => {
                console.log(e);
                this.cardsTable.loading = false;
                this.errors.push(e)
            })
        },

        parseChannelCfg(channels){
            var hasMoto = channels.motoChannel !== null && channels.motoChannel !== '';
            var hasEcom = channels.ecomChannel !== null && channels.ecomChannel !== '';

            switch (true) {
                case hasMoto && hasEcom:
                    this.cfgChannel = 'MOTO:ECOM';
                    break;
                case hasMoto:
                    this.cfgChannel = 'MOTO';
                    break;
                case hasEcom:
                    this.cfgChannel = 'ECOM';
                    break;
                default:
                    this.cfgChannel = null;
                    break;
            }

            // Debug
            if (this.client.id == 13386 && this.client.email == 'luke.munday@totalprocessing.com') {
                this.cfgChannel = 'All';
            }
        },

        getCards() {
            this.cardsTable.loading = true;
            axios({
                method: 'post',
                url: 'get-cards',
                data: { clientId: this.$route.params.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.cardsTable.loading = false;
                    this.cardsTable.archived = [];
                    this.cardsTable.unarchived = [];

                    this.filterStoredCards(response.data);
                    this.archiveCardSwitch();   
                })
                .catch(e => {
                    this.cardsTable.loading = false;
                    this.errors.push(e)
            })
        },

        filterStoredCards(data) {
            var ct = this.cardsTable;

            data.filter(e => {
                e.expMonth = e.expMonth < 10 ? `0${e.expMonth}` : e.expMonth;

                if(e.status == 1) {
                    this.appendCards(e, ct.unarchived);
                }
                else {
                    this.appendCards(e, ct.archived);
                }
            });
        },

        appendCards(e, dest) {
            if(e.expYear > moment().format('YYYY')) {
                dest.push({ state: 'active', card: e });
            }
            else if(e.expYear == moment().format('YYYY') && e.expMonth >= moment().format('MM')) {
                dest.push({ state: 'active', card: e });
            }
            else {
                dest.push({ state: 'expired', card: e });
            }
        },

        archiveCardSwitch() {
            if(this.cardsTable.showArchived) {
                this.cardsTable.data = this.cardsTable.archived;
            }
            else {
                this.cardsTable.data = this.cardsTable.unarchived;
            }
        },
        archiveCard(e) {
            axios({
                method: 'post',
                url: 'archive-card',
                data: { id: e.card.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.getCards();
                })
                .catch(e => {
                    this.errors.push(e)
            })
        },
        registerCardDialog() {
            this.showRegisterCardComponent = true;
        },
        registerCardDialog2() {
            this.showRegisterCardCnpComponent = true;
        },
        sendRegLinkDialog() {
            this.showRegisterCardLinkComponent = true;
        },
        createScheduleDialog(e) {
            this.createScheduleComponent.show = true;
            this.createScheduleComponent.data = e.card;
        },
        takePaymentDialog(e) {
            this.takePaymentComponent.show = true;
            this.takePaymentComponent.data = e.card;
        },
        scheduleSummary(e) {
            console.log(e);
            axios({
                method: 'post',
                url: 'get-schedule-summary',
                data: { scheduleId: e.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.scheduleSummaryComponent.show = true;
                    this.scheduleSummaryComponent.data = response.data;
                })
                .catch(e => {
                    this.errors.push(e)
            })
        },
        getSchedules() {
            this.schedulesTable.loading = true;
            axios({
                method: 'post',
                url: 'get-schedules',
                data: { clientId: this.$route.params.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    console.log(response.data);
                    this.schedulesTable.data = response.data;
                    this.schedulesTable.loading = false;
                })
                .catch(e => {
                    this.schedulesTable.loading = false;
                    this.errors.push(e)
            })
        },
        getProducts() {
            axios({
                method: 'post',
                url: 'get-products',
                data: { merchantId: this.client.merchantId },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.client.product = response.data;
                    console.log(this.client.product);
                })
                .catch(e => {
                    this.errors.push(e)
            })
        },
        terminate(e) {
            axios({
                method: 'post',
                url: 'terminate-schedule',
                data: { scheduleId: e.id, clientId: this.$route.params.id, userId: this.$store.state.user.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.getSchedules();
                    this.getClientLog();
                })
                .catch(e => {
                    this.errors.push(e)
            })
        },
        updateClient(e) {
            e.preventDefault();

            if(this.$refs.form.validate()) {
                axios({
                    method: 'post',
                    url: 'update-client',
                    data: { client: this.client, userId: this.$store.state.user.id },
                    config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                    })
                    .then(response => {
                        this.getClientLog();
                    })
                    .catch(e => {
                        this.errors.push(e)
                })
            }
        },
        accessed() {
            axios({
                method: 'post',
                url: 'add-client-access',
                data: { clientId: this.$route.params.id, userId: this.$store.state.user.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.getClientLog();
                })
                .catch(e => {
                    this.errors.push(e)
            })
        },
        getClientLog() {
            axios({
                method: 'post',
                url: 'get-client-log',
                data: { clientId: this.$route.params.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.client.client_log = response.data;
                })
                .catch(e => {
                    this.errors.push(e)
            })
        },
        getPayments() {
            this.paymentsTable.loading = true;
            axios({
                method: 'post',
                url: 'get-payments',
                data: { clientId: this.$route.params.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    console.log(response.data);
                    this.paymentsTable.loading = false;
                    this.paymentsTable.data = response.data;
                })
                .catch(e => {
                    this.paymentsTable.loading = false;
                    this.errors.push(e)
            })
        },
        refund(e) {
            axios({
                method: 'post',
                url: 'refund-live',
                data: { channelUuid: e.channelUuid,
                        uuid: e.uuid,
                        amount: e.amount,
                        currency: e.currency,
                        clientId: this.$route.params.id,
                        userId: this.$store.state.user.id,
                        transaction: e },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.getPayments();
                })
                .catch(e => {
                    this.errors.push(e)
                    this.getPayments();
            })
        },
        sendRegLink() {
            console.log(this.client);
            axios({
                method: 'post',
                url: 'send-reg-link',
                data: { client: this.client, userId: this.$store.state.user.id },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    
                })
                .catch(e => {
                    this.errors.push(e)
            })
        },
        changeCard(e) {
            this.cardsTable.selectedCard = e;
            this.showChangeCardComponent = true;
        },
        mobileCheck(e) {
            if(e) {
                // Allow UK and international numbers (e.164 format)

                // If length is 0 return true
                if (e.length == 0){
                    return true;
                }

                // If length is 11 and starts with a 0 return true
                if (e.length == 11 && e.startsWith('07')){
                    return true;
                }

                // If match e.164 format return true
                var re = new RegExp("^[+][1-9][0-9]{1,14}$");
                if (re.test(e)) {
                    return true;
                }

                // If here, then return invalid phone number
                return 'Invalid mobile number';
            }
            else {
                return true;
            }
        },
        validEmail(e)  {
            let pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return pattern.test(e) || e.length == 0 ? true : 'Invalid email address.'
        },
        getRegStatus() {
            axios({
            method: 'post',
            url: `get-reg-pay-status-live`,
            data: { path: this.path,
                    token: this.$route.params.id,
                    entityId: this.channels.motoChannel,
                    client: this.client,
                    userId: this.$store.state.user.id, },
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                if(response.data.result.code == '000.000.000') {
                    this.getCards();
                    this.snackbars.cardAdded = true;
                }
                else{
                    ({code: this.snackbars.cardRegErr.code, description: this.snackbars.cardRegErr.description} = response.data.result);
                    this.snackbars.cardRegErr.show = true
                }
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
    }
}
</script>

<style>
    .archive-switch label {
        font-size: 14px;
    }
</style>